/**
 * F-073 / F-078 — Profile sync + nickname management.
 *
 * Sign-in policy (F-078):
 *   - First login → INSERT (display_name = Google name, avatar_url = Google avatar)
 *   - Subsequent logins → UPDATE avatar_url only — preserves any nickname
 *     the user may have set since.
 *
 * `updateNickname` is the explicit user-action path: trim + 1~20 char
 * validation, then UPDATE display_name only.
 */

import type { SupabaseClient } from "@supabase/supabase-js";

import type { AuthUser } from "./auth-controller.ts";

const NICKNAME_MIN = 1;
const NICKNAME_MAX = 20;

/**
 * F-083 — Best-effort ISO 3166-1 alpha-2 country code from a BCP-47 locale.
 * `ko-KR` → `KR`, `en` → `US` (fallback), unrecognized → `null`.
 */
const LANGUAGE_DEFAULT_COUNTRY: Record<string, string> = {
  ko: "KR",
  ja: "JP",
  zh: "CN",
  en: "US",
  fr: "FR",
  de: "DE",
  es: "ES",
  pt: "BR",
  ru: "RU",
  it: "IT",
  nl: "NL",
};

export function detectCountry(locale: string | undefined | null): string | null {
  if (!locale) return null;
  const parts = locale.split(/[-_]/);
  // Walk subtags right-to-left looking for a 2-letter region code.
  for (let i = parts.length - 1; i >= 1; i -= 1) {
    const part = parts[i] ?? "";
    if (part.length === 2 && /^[A-Za-z]{2}$/.test(part)) {
      return part.toUpperCase();
    }
  }
  // Fallback — language → likely country.
  const lang = parts[0]?.toLowerCase();
  if (!lang) return null;
  return LANGUAGE_DEFAULT_COUNTRY[lang] ?? null;
}

/** ISO 3166-1 alpha-2 → 🇺🇸 emoji via Unicode regional indicator code points. */
export function countryToFlag(iso: string | null | undefined): string {
  if (!iso || iso.length !== 2) return "";
  const upper = iso.toUpperCase();
  if (!/^[A-Z]{2}$/.test(upper)) return "";
  const codePoints = upper.split("").map((c) => 0x1f1a5 + c.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

export async function syncProfile(
  client: SupabaseClient,
  user: AuthUser,
): Promise<void> {
  // F-083 — best-effort country auto-detect from the browser locale.
  const detectedCountry =
    typeof navigator !== "undefined"
      ? detectCountry(navigator.language)
      : null;

  // Look up existing row first — preserves any user-edited nickname.
  const { data: existing } = await client
    .from("profiles")
    .select("id, country")
    .eq("id", user.id)
    .maybeSingle();

  if (existing) {
    // Returning user — refresh avatar (Google may have changed it) and,
    // if country is still missing, fill it from the locale. Never
    // overwrite display_name or a country the user has already set.
    const updates: Record<string, unknown> = {
      avatar_url: user.avatarUrl ?? null,
    };
    const existingCountry = (existing as { country?: string | null }).country;
    if (!existingCountry && detectedCountry) {
      updates["country"] = detectedCountry;
    }
    await client.from("profiles").update(updates).eq("id", user.id);
    return;
  }

  // First login — seed display_name with the Google name + detected country.
  await client.from("profiles").insert({
    id: user.id,
    display_name: user.displayName.slice(0, 64),
    avatar_url: user.avatarUrl ?? null,
    country: detectedCountry,
  });
}

export interface NicknameValidationResult {
  ok: boolean;
  nickname: string;
  reason?: "empty" | "too_long";
}

export function validateNickname(input: string): NicknameValidationResult {
  const trimmed = input.trim();
  if (trimmed.length < NICKNAME_MIN) {
    return { ok: false, nickname: trimmed, reason: "empty" };
  }
  if (trimmed.length > NICKNAME_MAX) {
    return { ok: false, nickname: trimmed, reason: "too_long" };
  }
  return { ok: true, nickname: trimmed };
}

export async function updateNickname(
  client: SupabaseClient,
  userId: string,
  nickname: string,
): Promise<{ ok: true; nickname: string } | { ok: false; reason: string }> {
  const v = validateNickname(nickname);
  if (!v.ok) return { ok: false, reason: v.reason ?? "invalid" };
  const { error } = await client
    .from("profiles")
    .update({ display_name: v.nickname })
    .eq("id", userId);
  if (error) return { ok: false, reason: error.message };
  return { ok: true, nickname: v.nickname };
}

export const __testing = { NICKNAME_MIN, NICKNAME_MAX };
